import hslToHex from 'hsl-to-hex';

const hexColorRegex = /^#[\da-fA-F]{6}$/;

export function getBrightnessFromHex(color: string): number | undefined {
  if (!hexColorRegex.test(color)) return undefined;

  color = color.replace('#', '');
  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);
  return Math.sqrt(0.299 * Math.pow(r, 2) + 0.587 * Math.pow(g, 2) + 0.114 * Math.pow(b, 2));
}

export function getContrastColor(color: string): string {
  const brightness = getBrightnessFromHex(color);
  if (brightness === undefined) return '#000000';
  return brightness > 130 ? '#000000' : '#ffffff';
}

export function randomColor(): string {
  return hslToHex(Math.random() * 360, 90, 80);
}

export function multiplyHsl(
  color: string,
  factor: {
    h?: number;
    s?: number;
    l?: number;
  },
): string {
  const { h, s, l } = hexToHsl(color);
  return hslToHex(h * 360 * (factor.h ?? 1), s * 100 * (factor.s ?? 1), l * 100 * (factor.l ?? 1));
}

export function hexToHsl(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (!result) {
    console.warn('Invalid hex color', hex);
    return { h: 0, s: 0, l: 0 };
  }

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  // max === min => achromatisch, h und s bleiben 0
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return { h, s, l };
}

export function colorAsGradient(
  color: string,
  options?: {
    opacityA: number;
    opacityB: number;
  },
): string {
  const a = withAlpha(color, options?.opacityA ?? 1);
  const b = withAlpha(adjustHsl(color, { s: 0.8, l: 1.2 }), options?.opacityB ?? 1);
  return `linear-gradient(15deg, ${a} 0%, ${b} 100%)`;
}

export function adjustHsl(
  color: string,
  factor: {
    h?: number;
    s?: number;
    l?: number;
  },
): string {
  const { h, s, l } = hexToHsl(color);
  return hslToHex(h * 360 * (factor.h ?? 1), s * 100 * (factor.s ?? 1), l * 100 * (factor.l ?? 1));
}

export function withAlpha(hexColor: string, alpha: number): string {
  const color = hexColor.replace('#', '');
  return `#${color}${Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0')}`;
}

export function toRgba(color: string, alpha = 0.65): string {
  let hex = color.replace('#', '').toLowerCase();
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  return color;
}
